import { httpGet, httpPost, httpPut } from '../lib/http';

const BASE = '/api/silent-mentor';

// Simple localStorage cache helpers (web)
function cacheKey(trigger, placement) {
  return `silent_nudges:${trigger}:${placement}`;
}

function getCache(trigger, placement) {
  try {
    const key = cacheKey(trigger, placement);
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed.expiresAt && new Date(parsed.expiresAt) > new Date()) return parsed.value;
    localStorage.removeItem(key);
    return null;
  } catch (e) {
    return null;
  }
}

function setCache(trigger, placement, value, ttlMs = 6 * 60 * 60 * 1000) {
  try {
    const key = cacheKey(trigger, placement);
    const payload = { value, expiresAt: new Date(Date.now() + ttlMs).toISOString() };
    localStorage.setItem(key, JSON.stringify(payload));
  } catch (e) {}
}

// Event queue + batch flush (in-memory)
const EVENT_QUEUE = [];
let FLUSH_TIMER = null;
const FLUSH_INTERVAL_MS = 30 * 1000; // 30s

async function flushEvents() {
  if (EVENT_QUEUE.length === 0) return { success: true };
  const batch = EVENT_QUEUE.splice(0, 50);
  try {
    const res = await httpPost(`${BASE}/events`, { events: batch });
    // If server accepted, return; if not, re-queue
    if (!res || !res.ok) {
      // push back (simple strategy)
      EVENT_QUEUE.unshift(...batch);
      return { success: false };
    }
    return { success: true };
  } catch (e) {
    EVENT_QUEUE.unshift(...batch);
    return { success: false };
  }
}

function ensureFlushInterval() {
  if (FLUSH_TIMER) return;
  FLUSH_TIMER = setInterval(() => {
    flushEvents().catch(() => {});
  }, FLUSH_INTERVAL_MS);
}

export async function getNudges({ trigger = 'home_feed', placement = 'home_feed_card', limit = 2, force = false } = {}) {
  // Cache-first (to limit backend QPS)
  if (!force) {
    const cached = getCache(trigger, placement);
    if (cached) return { data: { nudges: cached } };
  }

  const params = new URLSearchParams();
  params.set('trigger', trigger);
  params.set('placement', placement);
  params.set('limit', String(limit));

  const url = `${BASE}/nudges?${params.toString()}`;
  const res = await httpGet(url);
  if (!res || !res.ok) {
    // Try POST fallback
    try {
      const body = { trigger, placement, limit };
      const postRes = await httpPost(`${BASE}/nudges/query`, body);
      if (postRes && postRes.ok) {
        const json = await postRes.json();
        setCache(trigger, placement, json?.data?.nudges || []);
        return json;
      }
      // surface error
      const text = postRes ? await postRes.clone().text() : 'no-response';
      throw new Error(text);
    } catch (err) {
      throw new Error(err.message || 'Failed to fetch nudges');
    }
  }

  const json = await res.json();
  setCache(trigger, placement, json?.data?.nudges || []);
  return json;
}

export async function postEvent(event) {
  // Attach client-side timestamp and push to queue; critical events flush immediately
  const e = { ...event, clientTimestamp: new Date().toISOString() };
  EVENT_QUEUE.push(e);
  ensureFlushInterval();
  // Flush immediately for click events
  if (e.eventType === 'nudge_clicked' || e.eventType === 'micro_action_completed') {
    await flushEvents();
  }
  return { success: true };
}

export async function getPreferences() {
  const res = await httpGet(`${BASE}/preferences`);
  if (!res || !res.ok) throw new Error('Failed to get preferences');
  return res.json();
}

export async function updatePreferences(preferences) {
  const res = await httpPut(`${BASE}/preferences`, { preferences });
  if (!res || !res.ok) throw new Error('Failed to update preferences');
  return res.json();
}

export default { getNudges, postEvent, getPreferences, updatePreferences };

